import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  Form,
  FormOptions,
  RowCustomAction,
  Section,
  SelectOption,
  UserPermission,
  useNotify
} from '@globaleris/ui.library'
import { CloudDownload, Delete, Edit } from '@material-ui/icons'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumTipoDocumento } from '../../../../../api'
import { AtlasDocumentModel } from '../../../../../api/Argentia/endpoints/documents/model/atlasDocument'
import { AllowedFileTypes } from '../../../../../config/allowedFileTypes'
import { DropzoneDialog } from '../../../../../components/shared/dropzoneDialog'
import { StandardCard } from '../../../../../components/shared/standardCard/standardCard'
import { getCustomerDomain } from '../../../../../redux'

export enum AtlasDocumentGroupKind {
  Document = 'document',
  Url = 'url'
}

type AtlasDocumentLanguage = 'ESP' | 'ENG'

interface AtlasDocumentInsertFormData {
  description?: string
  language: AtlasDocumentLanguage
  url?: string
}

export interface AtlasDocumentInsertPayload {
  type: EnumTipoDocumento
  description: string
  file?: File
  url?: string
}

export interface AtlasDocumentEditPayload extends AtlasDocumentInsertPayload {
  id: number
}

// única fuente de verdad kind+language -> EnumTipoDocumento; añadir un idioma es añadir una clave aquí
const ATLAS_DOCUMENT_TYPE_BY_KIND: Record<
  AtlasDocumentGroupKind,
  Record<AtlasDocumentLanguage, EnumTipoDocumento>
> = {
  [AtlasDocumentGroupKind.Document]: {
    ESP: EnumTipoDocumento.DocumentoAtlasESP,
    ENG: EnumTipoDocumento.DocumentoAtlasENG
  },
  [AtlasDocumentGroupKind.Url]: {
    ESP: EnumTipoDocumento.UrlAtlasESP,
    ENG: EnumTipoDocumento.UrlAtlasENG
  }
}

const getAtlasDocumentType = (
  kind: AtlasDocumentGroupKind,
  language: AtlasDocumentLanguage
): EnumTipoDocumento => ATLAS_DOCUMENT_TYPE_BY_KIND[kind][language]

// idioma derivado del mismo diccionario (evita mantener un segundo listado de tipos ENG en paralelo);
// el kind no importa aquí, por eso se aplanan ambos sub-diccionarios en un solo mapa type -> idioma
const ATLAS_LANGUAGE_BY_TYPE = Object.values(ATLAS_DOCUMENT_TYPE_BY_KIND).reduce(
  (acc, languages) => {
    Object.entries(languages).forEach(([language, type]) => {
      acc[type] = language as AtlasDocumentLanguage
    })
    return acc
  },
  {} as Partial<Record<EnumTipoDocumento, AtlasDocumentLanguage>>
)

const getAtlasDocumentLanguage = (type: EnumTipoDocumento): AtlasDocumentLanguage =>
  ATLAS_LANGUAGE_BY_TYPE[type] ?? 'ESP'

// solo formato (http/https), no se recorta ningún caracter: es una url externa, no un query de búsqueda
const isValidAtlasUrl = (value: string): boolean => {
  try {
    return ['http:', 'https:'].includes(new URL(value).protocol)
  } catch {
    return false
  }
}

// única fuente de las opciones de idioma, usada tanto por el form de alta como por la columna de la tabla
const getAtlasLanguageSelectOptions = (t: (key: string) => string): SelectOption[] => [
  {
    value: 'ESP',
    label: t('atlasDocuments:components.atlasDocumentGroupCard.table.columns.languageOptions.esp')
  },
  {
    value: 'ENG',
    label: t('atlasDocuments:components.atlasDocumentGroupCard.table.columns.languageOptions.eng')
  }
]

interface AtlasDocumentGroupCardProps {
  kind: AtlasDocumentGroupKind
  data: AtlasDocumentModel[]
  isLoading?: boolean
  onRowEdit?: (payload: AtlasDocumentEditPayload) => void
  onRowDelete?: (row: AtlasDocumentModel) => void
  onRowInsert?: (payload: AtlasDocumentInsertPayload) => void
  onRowDownload?: (row: AtlasDocumentModel) => void
}

// misma tabla G_DOCUMENTO para ambos kinds, solo cambia la columna extra (fecha vs url) y los textos
export const AtlasDocumentGroupCard: React.FC<AtlasDocumentGroupCardProps> = ({
  kind,
  data,
  isLoading,
  onRowEdit,
  onRowDelete,
  onRowInsert,
  onRowDownload
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'atlasDocuments'])
  const { error } = useNotify()
  const i18path = `atlasDocuments:components.atlasDocumentGroupCard.${kind}`

  const noData = data && data.length === 0

  const [insertDialogOpen, setInsertDialogOpen] = useState<boolean>(false)
  const [editingRow, setEditingRow] = useState<AtlasDocumentModel | null>(null)
  const [insertFormData, setInsertFormData] = useState<AtlasDocumentInsertFormData>({
    language: 'ESP'
  })

  const tableData = data.map(document => ({
    ...document,
    language: getAtlasDocumentLanguage(document.type),
    fileName: document.filename,
    url: document.externalUrl
  }))

  // #region Handlers Starts
  const handleOpenInsertDialog = async () => {
    setEditingRow(null)
    setInsertFormData({ description: '', language: 'ESP', url: undefined })
    setInsertDialogOpen(true)
    return true
  }

  const handleOpenEditDialog = async (row: AtlasDocumentModel) => {
    setEditingRow(row)
    setInsertFormData({
      description: row.description,
      language: getAtlasDocumentLanguage(row.type),
      url: row.externalUrl
    })
    setInsertDialogOpen(true)
    return true
  }

  const handleCloseInsertDialog = () => {
    setInsertDialogOpen(false)
    setEditingRow(null)
  }

  const handleSaveInsertDialog = (files: File[]) => {
    const type = getAtlasDocumentType(kind, insertFormData.language)
    const url = kind === AtlasDocumentGroupKind.Url ? insertFormData.url : undefined
    const description = insertFormData.description ?? ''

    if (kind === AtlasDocumentGroupKind.Url && (!url || !isValidAtlasUrl(url))) {
      error(t(`${i18path}.insertDialog.urlInvalid`))
      return
    }

    if (editingRow) {
      onRowEdit?.({ id: editingRow.id!, type, file: files[0], url, description })
    } else {
      onRowInsert?.({ type, file: files[0], url, description })
    }
    handleCloseInsertDialog()
  }
  // #endregion Handlers Ends

  // #region Functions Starts
  const getRowCustomActions = () => {
    const actions: RowCustomAction[] = []

    if (onRowDownload) {
      actions.push({
        icon: <CloudDownload fontSize="small" />,
        tooltip: t(`${i18path}.tooltips.download`),
        onClick: onRowDownload,
        userPermission: UserPermission.EDIT
      })
    }

    if (onRowEdit) {
      actions.push({
        icon: <Edit fontSize="small" />,
        tooltip: t(`${i18path}.tooltips.edit`),
        onClick: handleOpenEditDialog,
        userPermission: UserPermission.EDIT
      })
    }

    if (onRowDelete) {
      actions.push({
        icon: <Delete fontSize="small" />,
        tooltip: t(`${i18path}.tooltips.delete`),
        onClick: onRowDelete,
        userPermission: UserPermission.EDIT
      })
    }

    return actions
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  const insertFormSections: Section[] = [
    {
      title: t(`${i18path}.insertDialog.sectionTitle`),
      inputs: [
        {
          name: 'description',
          label: t('atlasDocuments:components.atlasDocumentGroupCard.table.columns.title'),
          type: DataType.TEXT,
          required: true,
          inputSize: 12,
          placeholder: t(`${i18path}.insertDialog.titlePlaceholder`),
          onChange: (value: string | number) =>
            setInsertFormData(prev => ({ ...prev, description: String(value) }))
        },
        {
          name: 'language',
          label: t('atlasDocuments:components.atlasDocumentGroupCard.table.columns.language'),
          type: DataType.SELECT,
          required: true,
          inputSize: kind === AtlasDocumentGroupKind.Url ? 6 : 12,
          selectOptions: getAtlasLanguageSelectOptions(t),
          // cambiar el idioma implicaría cambiar el EnumTipoDocumento del registro ya creado
          readOnly: !!editingRow,
          onChange: (value: string | number) =>
            setInsertFormData(prev => ({ ...prev, language: value as AtlasDocumentLanguage }))
        },
        ...(kind === AtlasDocumentGroupKind.Url
          ? [
              {
                name: 'url',
                label: t(`${i18path}.insertDialog.url`),
                type: DataType.TEXT,
                required: true,
                inputSize: 6 as const,
                placeholder: t(`${i18path}.insertDialog.urlPlaceholder`),
                onChange: (value: string | number) =>
                  setInsertFormData(prev => ({ ...prev, url: String(value) }))
              }
            ]
          : [])
      ]
    }
  ]

  const insertFormOptions: FormOptions = {
    elevation: 0,
    columns: 2,
    analytics: {
      customer: getCustomerDomain(),
      view: `atlas_${kind}_insert_form`
    }
  }
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const atlasDocumentGroupDataTableColumns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML,
      columnWidth: 65
    },
    {
      name: 'description',
      label: t('atlasDocuments:components.atlasDocumentGroupCard.table.columns.title'),
      type: DataType.TEXT
    },
    {
      name: 'language',
      label: t('atlasDocuments:components.atlasDocumentGroupCard.table.columns.language'),
      type: DataType.SELECT,
      selectOptions: getAtlasLanguageSelectOptions(t)
    },
    kind === AtlasDocumentGroupKind.Url
      ? {
          name: 'url',
          label: t('atlasDocuments:components.atlasDocumentGroupCard.url.columns.url'),
          type: DataType.TEXT
        }
      : {
          name: 'fileName',
          label: t('atlasDocuments:components.atlasDocumentGroupCard.document.columns.file'),
          type: DataType.TEXT
        },
    {
      name: 'documentDate',
      label: t(`${i18path}.columns.date`),
      type: DataType.TEXT
    }
  ]

  const atlasDocumentGroupDataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    filterType: FilterType.ADVANCED,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: !!onRowInsert,
    customInsertEditModal: true,
    onRowInsert: onRowInsert ? handleOpenInsertDialog : undefined,
    pagination: true,
    analytics: {
      customer: getCustomerDomain(),
      view: `atlas_${kind}s_table`
    },
    rowCustomActions: getRowCustomActions()
  }
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends

  return (
    <>
      <StandardCard
        title={t(`${i18path}.title`)}
        isLoading={isLoading}
        showEmptyContent={noData}
        emptyCaption={t(`${i18path}.emptyCaption`)}
        emptyButtonCallback={onRowInsert ? handleOpenInsertDialog : undefined}
        emptyButtonCaption={onRowInsert ? t(`${i18path}.insertDialog.title`) : undefined}
      >
        <DataTable
          data={tableData}
          columns={atlasDocumentGroupDataTableColumns}
          options={atlasDocumentGroupDataTableOptions}
        />
      </StandardCard>

      {(onRowInsert || onRowEdit) && (
        <DropzoneDialog
          open={insertDialogOpen}
          title={editingRow ? t(`${i18path}.tooltips.edit`) : t(`${i18path}.insertDialog.title`)}
          onBack={handleCloseInsertDialog}
          onSave={handleSaveInsertDialog}
          requireFile={!editingRow && kind === AtlasDocumentGroupKind.Document}
          hideDropzone={
            kind === AtlasDocumentGroupKind.Url ||
            (kind === AtlasDocumentGroupKind.Document && !!editingRow)
          }
          compactChildren
          dropzoneProps={{
            filesLimit: 1,
            acceptedFiles: AllowedFileTypes.documents.extensions,
            maxFileSize: AllowedFileTypes.documents.maxSizeMB * 1024 * 1024,
            ...(kind === AtlasDocumentGroupKind.Document && {
              dropzoneText: t(`${i18path}.insertDialog.dropText`)
            })
          }}
        >
          <Form sections={insertFormSections} data={insertFormData} options={insertFormOptions} />
        </DropzoneDialog>
      )}
    </>
  )
}
